import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { googleAuth, GoogleAuthResponse } from '../services/google-auth';
import { userProfileService, UserProfile } from '../services/userProfileService';
import { API_CONFIG } from '../config/api';

// SVG placeholder for user pictures (no external network calls)
const PLACEHOLDER_AVATAR = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="150"%3E%3Crect width="150" height="150" fill="%234F46E5"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="60" fill="%23FFFFFF"%3E%F0%9F%91%A4%3C/text%3E%3C/svg%3E';

interface AuthContextType {
  user: any | null;
  session: any | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: any }>;
  signUp: (email: string, password: string, fullName: string, company?: string) => Promise<{ error?: any }>;
  signInWithGoogle: () => Promise<{ error?: any; user?: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error?: any }>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ error?: any }>;
  refreshProfile: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [session, setSession] = useState<any | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchUserProfile = async (brandId: string) => {
    try {
      const profile = await userProfileService.getUserProfile(brandId);
      setUserProfile(profile);
      console.log('✅ User profile loaded:', profile);
    } catch (error) {
      console.error('Failed to load user profile:', error);
      // Don't throw error - profile is optional
    }
  };

  // Check for existing session on load using cookies
  useEffect(() => {
    const initAuth = async () => {
      console.log('🔍 AuthContext: Checking for existing cookie-based session...');
      console.log('   Domain:', window.location.hostname);
      console.log('   Full URL:', window.location.href);

      // ── 1. Try real cookie-based AICOS session first ────────────────────
      // After the Mela bridge endpoint runs, real auth_token + brand_id
      // cookies exist and this branch picks them up — giving the bridged
      // Mela user a real backend identity (brand_id='mela-marketplace').
      try {
        const API_URL = API_CONFIG.BASE_URL;
        const response = await fetch(`${API_URL}/api/v1/auth/me`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        if (response.ok) {
          const userData = await response.json();
          const isBridged = userData.email === 'mela@aicommerce-os.internal';
          const user = {
            id: userData.user_id,
            brandId: userData.brand_id,
            email: userData.email,
            name: userData.email?.split('@')[0] || 'User',
            brand: userData.brand_name || userData.brand_id,
            isMelaBridge: isBridged,
          };
          setUser(user);
          setSession({ user, token: 'cookie-based' });
          if (isBridged && !localStorage.getItem('onboarding_completed')) {
            localStorage.setItem('onboarding_completed', 'true');
          }
          try {
            await fetchUserProfile(userData.brand_id);
          } catch (profileError) {
            console.error('⚠️ AuthContext: Failed to load profile:', profileError);
          }
          setLoading(false);
          return;
        }
        console.log('ℹ️ AuthContext: No valid AICOS cookie — checking Mela admin bridge fallback');
      } catch (error) {
        console.error('❌ AuthContext: Cookie session check error:', error);
      }

      // ── 2. Fallback: synthetic Mela bridge ─────────────────────────────
      // If the user is logged into Mela admin but the bridge endpoint
      // hasn't been called yet (e.g. they navigated directly to an AICOS
      // URL), let them through with a frontend-only synthetic identity so
      // pages render. Backend API calls will 401 in this state.
      try {
        const melaSessionRaw = localStorage.getItem('mela_admin_session');
        if (melaSessionRaw) {
          const melaSession = JSON.parse(melaSessionRaw);
          if (melaSession?.email) {
            const bridgedUser = {
              id: `mela-bridge-${melaSession.email}`,
              email: melaSession.email,
              name: melaSession.name || melaSession.email.split('@')[0],
              brand: 'Mela Marketplace',
              brandId: 'mela-marketplace',
              role: melaSession.role || 'admin',
              isMelaBridge: true,
            };
            setUser(bridgedUser);
            setSession({ user: bridgedUser, token: 'mela-admin-bridge' });
            if (!localStorage.getItem('onboarding_completed')) {
              localStorage.setItem('onboarding_completed', 'true');
            }
            localStorage.setItem('brand_id', 'mela-marketplace');
            console.log('🌉 AICOS using synthetic Mela bridge (no cookie):', melaSession.email);
          }
        }
      } catch (e) {
        console.warn('Mela bridge fallback failed:', e);
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  // Refresh authentication state (useful after login)
  const refreshAuth = async () => {
    try {
      const API_URL = API_CONFIG.BASE_URL;
      const response = await fetch(`${API_URL}/api/v1/auth/me`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        console.log('🔄 AuthContext: Session refreshed:', userData);
        
        const user = {
          id: userData.user_id,
          brandId: userData.brand_id,
          email: userData.email,
          name: userData.email?.split('@')[0] || 'User',
          brand: userData.brand_name || userData.brand_id
        };
        
        setUser(user);
        setSession({ user, token: 'cookie-based' });
        
        // Fetch full profile
        try {
          await fetchUserProfile(userData.brand_id);
        } catch (profileError) {
          console.error('⚠️ AuthContext: Failed to load profile during refresh:', profileError);
        }
      } else {
        setUser(null);
        setSession(null);
        setUserProfile(null);
      }
    } catch (error) {
      console.error('❌ AuthContext: Refresh error:', error);
      setUser(null);
      setSession(null);
      setUserProfile(null);
    }
  };

  const isAuthenticated = !!user;

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    
    try {
      const API_URL = API_CONFIG.BASE_URL;
      const response = await fetch(`${API_URL}/api/v1/auth/login`, {
        method: 'POST',
        credentials: 'include', // Include cookies
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        console.log('✅ Login successful with cookies');
        
        const userData = {
          id: data.user_id,
          email: email,
          name: email.split('@')[0],
          brand: data.brand_id,
          brandId: data.brand_id
        };
        
        setUser(userData);
        setSession({ user: userData, token: 'cookie-based' });
        
        // Fetch profile after successful login
        await fetchUserProfile(data.brand_id);
        setLoading(false);
        return { error: null };
      } else {
        setLoading(false);
        return { error: { message: data.detail || 'Login failed' } };
      }
    } catch (error: any) {
      setLoading(false);
      return { error: { message: error.message || 'Network error' } };
    }
  };

  const signUp = async (email: string, password: string, fullName: string, company?: string) => {
    setLoading(true);
    
    try {
      // Split fullName into first and last name
      const [firstName, ...lastNameParts] = fullName.split(' ');
      const lastName = lastNameParts.join(' ');

      const API_URL = API_CONFIG.BASE_URL;
      const response = await fetch(API_URL + '/api/onboarding/signup', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          password,
          firstName,
          lastName,
          company: company || `${firstName}'s Company`
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        console.log('✅ Signup successful with cookies');
        
        const userData = {
          id: data.user_id,
          email: email,
          name: fullName,
          brand: data.brand_id,
          brandId: data.brand_id
        };
        
        setUser(userData);
        setSession({ user: userData, token: 'cookie-based' });
        
        await fetchUserProfile(data.brand_id);
        setLoading(false);
        return { error: null };
      } else {
        setLoading(false);
        return { error: { message: data.detail || 'Signup failed' } };
      }
    } catch (error: any) {
      setLoading(false);
      return { error: { message: error.message || 'Network error' } };
    }
  };

  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      // Demo mode - simulate Google login for development
      const demoGoogleUser = {
        id: 'google-demo-user-123',
        email: 'demo.google@aicommerce.com',
        name: 'Demo Google User',
        picture: PLACEHOLDER_AVATAR,
        brand: 'Google Demo Brand',
        brandId: 'google-demo-brand-123',
        authProvider: 'google',
        isDemoMode: true
      };
      
      // Simulate loading time
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setUser(demoGoogleUser);
      setSession({ user: demoGoogleUser, token: 'google-demo-token-123' });
      setLoading(false);
      return { error: null, user: demoGoogleUser };
    } catch (error) {
      setLoading(false);
      return { error: error instanceof Error ? error.message : 'Google authentication failed' };
    }
  };

  const signOut = async () => {
    const isMelaBridge = user?.isMelaBridge;
    try {
      if (!isMelaBridge) {
        // Only call AICOS logout for real AICOS users — bridge users have no cookie
        const API_URL = API_CONFIG.BASE_URL;
        await fetch(`${API_URL}/api/v1/auth/logout`, {
          method: 'POST',
          credentials: 'include'
        });
      }
    } catch (error) {
      console.error('Logout request failed:', error);
      // Continue with local logout even if backend fails
    }

    // Clear AICOS state. Mela admin session in localStorage is left alone — the
    // user is still logged into Mela admin even if they "sign out" of AICOS.
    setUser(null);
    setSession(null);
    setUserProfile(null);
  };

  const resetPassword = async (email: string) => {
    try {
      const API_URL = API_CONFIG.BASE_URL;
      const response = await fetch(`${API_URL}/api/v1/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        return { error: null };
      } else {
        const data = await response.json();
        return { error: { message: data.detail || 'Reset failed' } };
      }
    } catch (error: any) {
      return { error: { message: error.message || 'Network error' } };
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      if (!user?.email) {
        return { error: { message: 'User not authenticated' } };
      }

      const API_URL = API_CONFIG.BASE_URL;
      const response = await fetch(`${API_URL}/api/v1/auth/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: user.email,
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      if (response.ok) {
        return { error: null };
      } else {
        const data = await response.json();
        return { error: { message: data.detail || 'Password change failed' } };
      }
    } catch (error: any) {
      return { error: { message: error.message || 'Network error' } };
    }
  };

  const refreshProfile = async () => {
    if (user?.brandId) {
      await fetchUserProfile(user.brandId);
    }
  };

  const value: AuthContextType = {
    user,
    session,
    userProfile,
    loading,
    isAuthenticated,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    resetPassword,
    changePassword,
    refreshProfile,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;